<script lang="ts" setup>
import { useDefaultStore } from "@/store";
import api from "@/api";
import { useToast } from "primevue/usetoast";
import { $t } from "@/i18n";
import { debounce } from "@/assets/js/helpers";
import { useConfirm } from "primevue/useconfirm";
// import type { IUserList, IPermissions } from "@/api/types";
import { useSessionStore } from "@/store/session";
import type { TCategory } from "@/api/types";

definePage({
	name: "ManageCategories",
	meta: {
		name: "Manage Catgories",
	},
});

const route = useRoute();
const router = useRouter();

const confirm = useConfirm();
const toast = useToast();
const store = useDefaultStore();
const sessionStore = useSessionStore();

const searchValue = ref<string | null>(null);
const selectedProduct = ref();
const loadingDelete = ref(false);
const editingRows = ref([]);

const selectedParentCatId = computed<number | null>(() => {
	const raw = route.query.cat_id_parent;
	const value = Array.isArray(raw) ? raw[0] : raw;
	if (value == null || value === "") return null;

	const n = Number(value);
	return Number.isFinite(n) ? n : null;
});

async function onCategoryNameClick(cat: TCategory) {
	if (!cat.cat_is_parent) return;

	const catId = cat.cat_id;

	const current = selectedParentCatId.value;

	const nextQuery = { ...route.query };

	if (current === catId) {
		// toggle off
		delete (nextQuery as any).cat_id_parent;
	} else {
		(nextQuery as any).cat_id_parent = String(catId);
	}

	// await router.replace({ query: nextQuery });
	await router.push({ query: nextQuery });
}

onMounted(async () => {
	await store.getCategories({
		search: searchValue.value,
		cat_id_parent: selectedParentCatId.value,
	});
});

watch(
	() => selectedParentCatId.value,
	async (catId, oldCatId) => {
		if (catId === oldCatId) return;

		await store.getCategories({
			search: searchValue.value,
			cat_id_parent: catId,
		});
	},
);

const onRowEditSave = async (event) => {
	await editCategory(event.newData.cat_id, event.newData.cat_name);
};

const getTagsDebounce = () => {
	debounce(
		store.getCategories({
			search: searchValue.value,
			cat_id_parent: selectedParentCatId.value,
		}),
		300,
		true,
	);
};

async function deleteCategory(cat_id: number) {
	loadingDelete.value = true;
	const params = {
		cat_id,
	};
	try {
		const res = await api.categoriesRemove(params);
		toast.add({
			severity: "success",
			summary: $t("app.manageTags.success.deleteTag.title"),
			detail: $t("app.manageTags.success.deleteTag.msg"),
			life: 3000,
		});
		await store.getCategories({
			search: searchValue.value,
			cat_id_parent: selectedParentCatId.value,
		});
		console.log(res.data.data);
	} catch (err: any) {
		console.warn(err.message);
		toast.add({
			severity: "error",
			summary: $t("app.postForm.error.default.title"),
			detail: $t("app.postForm.error.default.msg"),
			life: 3000,
		});
	} finally {
		loadingDelete.value = false;
	}
}

const confirmDelete = (cat_id: number) => {
	confirm.require({
		message: $t("app.manageTags.deleteTag.msg"),
		header: $t("app.manageTags.deleteTag.header"),
		rejectLabel: $t("app.basic.cancel"),
		rejectProps: {
			label: $t("app.basic.cancel"),
			severity: "secondary",
			outlined: true,
		},
		acceptProps: {
			label: $t("app.basic.delete"),
			severity: "danger",
		},
		accept: async () => {
			await deleteCategory(cat_id);
		},
	});
};

async function editCategory(cat_id: number, cat_name: string | null = null) {
	const params = {
		cat_id,
		cat_name,
	};

	try {
		const res = await api.categoriesEdit(params);
		toast.add({
			severity: "success",
			summary: $t("app.manageTags.success.editLabel.title"),
			detail: $t("app.manageTags.success.editLabel.msg"),
			life: 3000,
		});
		await store.getCategories({
			search: searchValue.value,
			cat_id_parent: selectedParentCatId.value,
		});
		console.warn(res.data);
	} catch (err: any) {
		console.warn(err.message);
		toast.add({
			severity: "error",
			summary: $t("app.postForm.error.default.title"),
			detail: $t("app.postForm.error.default.msg"),
			life: 3000,
		});
	}
}

function openCreateCategoryDialog(cat: TCategory) {
	store.newCategoryParent = cat;

	store.createCategoryVisible = true;
}
</script>

<template>
	<div class="admin-section-outer-wrapper">
		<div class="admin-section-inner-wrapper">
			<div class="admin-section-header-wrapper">
				<div class="admin-section-header-heading">
					{{ $t("app.manageTags.title") }}
				</div>
				<div class="admin-section-header-filters-wrapper">
					<IconField
						:pt="{
							root: {
								style: 'width: 333px; max-width: 90%; height: 38px;',
							},
						}"
					>
						<InputIcon>
							<i-fa-magnifying-glass />
						</InputIcon>
						<InputText
							v-model="searchValue"
							@value-change="getTagsDebounce"
							:placeholder="$t('app.manageTags.searchPlaceholder')"
							fluid
						/>
					</IconField>
					<create-tag
						@create-tag="
							store.getCategories({
								search: searchValue,
								cat_id_parent: selectedParentCatId,
							})
						"
					/>
				</div>
			</div>
			<div
				class="admin-section-content-wrapper"
				style="max-width: 600px"
			>
				<DataTable
					v-if="!store.categoryList"
					:value="[
						{ id: 1 },
						{ id: 2 },
						{ id: 3 },
						{ id: 4 },
						{ id: 5 },
						{ id: 6 },
						{ id: 7 },
						{ id: 8 },
						{ id: 9 },
						{ id: 10 },
					]"
					stripedRows
					scrollable
					scrollHeight="500px"
				>
					<Column
						:header="$t('app.manageTags.nameLabel')"
						style="width: 100%"
					>
						<template #body>
							<Skeleton height="29px"></Skeleton>
						</template>
					</Column>
					<Column style="width: 82px">
						<template #body>
							<Button
								severity="danger"
								variant="outlined"
								rounded
							>
								<template #icon>
									<i-fa-trash-can></i-fa-trash-can>
								</template>
							</Button>
						</template>
					</Column>
				</DataTable>
				<DataTable
					v-else
					v-model:editingRows="editingRows"
					:value="store.categoryList"
					stripedRows
					selectionMode="single"
					v-model:selection="selectedProduct"
					scrollable
					scrollHeight="500px"
					:virtualScrollerOptions="{ itemSize: 50 }"
					editMode="row"
					@row-edit-save="onRowEditSave"
				>
					<Column
						field="cat_name"
						:header="$t('app.manageTags.nameLabel')"
						style="width: 100%"
					>
						<template #body="{ data }">
							<span
								class="cat-name"
								role="button"
								tabindex="0"
								@click.stop="onCategoryNameClick(data)"
							>
								{{ data.cat_name }} ({{ data.entities_count }})
							</span>
						</template>

						<template #editor="{ data, field }">
							<InputText v-model="data[field]" />
						</template>
					</Column>
					<Column
						v-if="sessionStore.can('edit-tag')"
						:rowEditor="true"
						style="min-width: 120px"
						bodyStyle="text-align:end"
					></Column>
					<Column
						:rowEditor="true"
						style="min-width: 82px"
						bodyStyle="text-align:end"
					>
						<template #body="{ data }">
							<Button
								variant="outlined"
								rounded
								@click="openCreateCategoryDialog(data)"
								:loading="loadingDelete"
							>
								<template #icon>
									<i-fa-plus></i-fa-plus>
								</template>
							</Button>
						</template>
					</Column>
					<Column
						v-if="sessionStore.can('delete-tag')"
						style="display: flex; justify-content: center; width: 82px"
					>
						<template #header><i-fa-trash-can></i-fa-trash-can></template>
						<template #body="{ data }">
							<Button
								v-if="!data.cat_is_parent"
								severity="danger"
								variant="outlined"
								rounded
								@click="confirmDelete(data.cat_id)"
								:loading="loadingDelete"
							>
								<template #icon>
									<i-fa-trash-can></i-fa-trash-can>
								</template>
							</Button>
						</template>
					</Column>
				</DataTable>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
.p-virtualscroller-inline {
	&::-webkit-scrollbar {
		background-color: $scrollbar-color;
		width: 5px;
		height: 5px;
	}

	&::-webkit-scrollbar-thumb {
		background-color: $scrollbar-color-thumb;
		border-radius: 5px;
	}
}

.p-datatable-column-title {
	font-size: 11px;
	font-weight: 700;
	line-height: 12.89px;
	color: $text-color-lighter;
}
</style>
